import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App as AntApp,
  Spin,
  Typography,
  Button,
  Space,
  Table,
  Tag,
  Tooltip,
  Alert,
} from "antd";
import axios from "axios";
import { Session } from "../storage/session";
import { useAuth } from "../context/AuthContext";
import { createMessagingClient } from "../api/messagingClient";
import { Encrypt } from "../utils/encryption";
import { userManagementApi } from "../config/api";
import {
  EXISTING_WHATSAPP_OAUTH_STATE_PREFIX,
} from "../utils/facebookSdk";
import {
  OAUTH_REDIRECT_URI
} from "../config/mappedEnv"

const STATE_MAX_AGE_MS = 20 * 60 * 1000;

const rowKey = (r) => `${r.phoneNumberId}__${r.wabaId}`;

export default function WhatsAppExistingOAuthCallback() {
  const navigate = useNavigate();
  const { setToken: setAuthToken } = useAuth();
  const { message } = AntApp.useApp();
  const ran = useRef(false);
  const [ui, setUi] = useState({
    mode: "loading",
    message: "",
    candidates: [],
    setupSessionId: null,
    selectedKey: null,
  });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error");
    const errDesc = params.get("error_description");

    if (err) {
      setUi({
        mode: "error",
        message: errDesc || err || "Facebook returned an error.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    if (!code || !state) {
      setUi({
        mode: "error",
        message: "Missing authorization code or state.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    const key = EXISTING_WHATSAPP_OAUTH_STATE_PREFIX + state;
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      setUi({
        mode: "error",
        message:
          "Invalid or expired login state. Open Configuration and use “Add Existing WhatsApp” again.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }
    sessionStorage.removeItem(key);

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setUi({
        mode: "error",
        message: "Corrupted OAuth state.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    if (Date.now() - (parsed.t || 0) > STATE_MAX_AGE_MS) {
      setUi({
        mode: "error",
        message: "OAuth session expired. Try again.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    const session = Session.get();
    if (!session?.token) {
      setUi({
        mode: "error",
        message: "You are not signed in. Log in, then link WhatsApp again.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    const redirectUri = OAUTH_REDIRECT_URI;
    if (!redirectUri) {
      setUi({
        mode: "error",
        message: "OAuth redirect URI is not configured.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          userManagementApi,
          {
            ...Encrypt({
              token: session.token,
              metaCode: code,
              redirectUri,
              oauthStyle: "existing",
            }),
            operationType: "whatsappWaDiscover",
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const candidates = res.data?.candidates || [];
        const setupSessionId = res.data?.setupSessionId;

        if (!setupSessionId || candidates.length === 0) {
          setUi({
            mode: "error",
            message:
              res.data?.message ||
              "No WhatsApp numbers were found for this Meta account.",
            candidates: [],
            setupSessionId: null,
            selectedKey: null,
          });
          return;
        }

        setUi({
          mode: "pick",
          message: "",
          candidates,
          setupSessionId,
          selectedKey: null,
        });
      } catch (e) {
        const msg = e.response?.data?.message || e.message || "Could not list WhatsApp numbers.";
        if (msg.includes("Missing Permission")) {
          message.error({ content: msg, key: "meta_permission_error" });
        }
        setUi({
          mode: "error",
          message: msg,
          candidates: [],
          setupSessionId: null,
          selectedKey: null,
        });
      }
    })();
  }, [message]);

  const finalize = async () => {
    const session = Session.get();
    const {
      setupSessionId,
      selectedKey,
      candidates,
    } = ui;
    if (
      !session?.token ||
      !setupSessionId ||
      !selectedKey ||
      ui.mode !== "pick"
    ) {
      return;
    }

    const selected = candidates.find((c) => rowKey(c) === selectedKey);
    if (!selected?.selectable) return;

    setUi((prev) => ({ ...prev, mode: "finalizing" }));
    try {
      const res = await axios.post(
        userManagementApi,
        {
          ...Encrypt({
            token: session.token,
            setupSessionId,
            phoneNumberId: selected.phoneNumberId,
            wabaId: selected.wabaId,
          }),
          operationType: "whatsappWaFinalize",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const newToken = res.data?.token;
      if (newToken) {
        Session.save({
          ...session,
          token: newToken,
          status: session.status || "success",
        });
        setAuthToken(newToken);
      }

      setUi({
        mode: "success",
        message: res.data?.message || "WhatsApp account linked.",
        candidates: [],
        setupSessionId: null,
        selectedKey: null,
      });
      setTimeout(() => navigate("/", { replace: true }), 900);
    } catch (e) {
      setUi({
        mode: "error",
        message:
          e.response?.data?.message ||
          e.message ||
          "Failed to finalize WhatsApp connection.",
        candidates: ui.candidates,
        setupSessionId: ui.setupSessionId,
        selectedKey: ui.selectedKey,
      });
    }
  };

  const pickerColumns = [
    {
      title: "Phone",
      key: "display",
      render: (_, r) => (
        <span>
          {r.displayPhoneNumber || r.phoneNumberId}
          {r.reason ? (
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              ({r.reason})
            </Typography.Text>
          ) : null}
        </span>
      ),
    },
    {
      title: "Mode",
      key: "mode",
      width: 110,
      render: (_, r) =>
        r.numberType === "test" ||
          String(r.accountMode || "").toUpperCase() === "SANDBOX" ? (
          <Tag color="orange">Test</Tag>
        ) : (
          <Tag color="green">Live</Tag>
        ),
    },
    {
      title: "WABA",
      key: "waba",
      ellipsis: true,
      render: (_, r) => (
        <Tooltip title={r.wabaId}>{String(r.wabaId || "").slice(0, 14)}…</Tooltip>
      ),
    },
  ];

  /* ---- Shared card wrapper for consistent look ---- */
  const cardStyle = {
    maxWidth: 720,
    margin: "80px auto 0",
    padding: "32px 28px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
  };

  const pageBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #e0f7fa 0%, #f0f2f5 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px",
  };

  const brandHeader = (
    <div style={{ textAlign: "center", marginBottom: 16 }}>
      <Typography.Title level={3} style={{ margin: 0, color: "#00a884" }}>
        Zibuzz
      </Typography.Title>
    </div>
  );

  if (ui.mode === "loading") {
    return (
      <div style={pageBg}>
        <div style={cardStyle}>
          {brandHeader}
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <Typography.Paragraph style={{ marginTop: 16 }}>
              Loading WhatsApp numbers from Meta…
            </Typography.Paragraph>
          </div>
        </div>
      </div>
    );
  }

  if (ui.mode === "finalizing") {
    return (
      <div style={pageBg}>
        <div style={cardStyle}>
          {brandHeader}
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <Typography.Paragraph style={{ marginTop: 16 }}>
              Connecting your number…
            </Typography.Paragraph>
          </div>
        </div>
      </div>
    );
  }

  if (ui.mode === "pick") {
    return (
      <div style={pageBg}>
        <div style={cardStyle}>
          {brandHeader}
          <Typography.Title level={4}>
            Pick your WhatsApp Business number
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            Choose your WhatsApp Business number. Test numbers are marked
            with an orange tag.
          </Typography.Paragraph>
          <Table
            rowKey={rowKey}
            size="small"
            style={{ marginTop: 16 }}
            pagination={ui.candidates.length > 8 ? { pageSize: 8 } : false}
            columns={pickerColumns}
            dataSource={ui.candidates}
            rowSelection={{
              type: "radio",
              selectedRowKeys: ui.selectedKey ? [ui.selectedKey] : [],
              onChange: (_keys, rows) => {
                const row = rows[0];
                setUi((prev) => ({
                  ...prev,
                  selectedKey: row ? rowKey(row) : null,
                }));
              },
            }}
            onRow={(record) => ({
              onClick: () => {
                setUi((prev) => ({
                  ...prev,
                  selectedKey: rowKey(record),
                }));
              },
            })}
          />
          <Space style={{ marginTop: 24 }}>
            <Button type="primary" disabled={!ui.selectedKey} onClick={finalize}>
              Connect this number
            </Button>
            <Button onClick={() => navigate("/", { replace: true })}>Cancel</Button>
          </Space>
        </div>
      </div>
    );
  }

  if (ui.mode === "success") {
    return (
      <div style={pageBg}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          {brandHeader}
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <Typography.Title level={4}>Successfully Connected!</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
            Your WhatsApp Business number is now linked to ZiBuzz.
          </Typography.Paragraph>

          <Alert
            message="Step 5: Finalize Billing"
            description={
              <div style={{ textAlign: "left" }}>
                Meta requires a payment method on your Business Portfolio to send messages beyond the free tier.
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    href="https://business.facebook.com/wa/manage/home/"
                    target="_blank"
                    style={{ padding: 0 }}
                  >
                    Open Meta Business Manager →
                  </Button>
                </div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => navigate("/", { replace: true })}
            >
              Back to Business Messenger
            </Button>
            <Button
              size="small"
              type="text"
              onClick={() => {
                const num = prompt("Enter a phone number to send a test message (with country code, e.g. 14155552671):");
                if (num) {
                  const token = localStorage.getItem("zibuzz_token");
                  const client = createMessagingClient(token);
                  if (client) {
                    client.sendReply(num, "Hello from ZiBuzz! Your WhatsApp integration is active. 🚀")
                      .then(() => message.success("Test message sent!"))
                      .catch((err) => message.error("Test failed: " + (err.response?.data?.message || err.message)));
                  }
                }
              }}
            >
              Send a test message
            </Button>
          </Space>
        </div>
      </div>
    );
  }

  return (
    <div style={pageBg}>
      <div style={cardStyle}>
        {brandHeader}
        <Typography.Title level={4}>Could not link WhatsApp</Typography.Title>
        <Typography.Paragraph>{ui.message}</Typography.Paragraph>
        <Space>
          <Button type="primary" onClick={() => navigate("/", { replace: true })}>
            Back to app
          </Button>
        </Space>
      </div>
    </div>
  );
}
